import { useMemo, useState } from 'react'
import { Card } from '@/components/ui/card'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts'
import {
  type Stage,
  STAGE_ORDER,
  STAGE_LABELS,
  STAGE_SHORT,
  STAGE_ACCENT,
  colorForChannel,
  labelForChannel,
  formatNumber,
  formatCurrency,
  formatDate,
  formatPct,
} from './_shared'

type Dashboard = {
  channels: string[]
  series: Array<{
    date: string
    perChannel: Record<Stage, Record<string, number>>
    total: Record<Stage, number>
  }>
  stageTotals: Record<Stage, number>
  dropoffs: Array<{
    from: Stage
    to: Stage
    fromCount: number
    toCount: number
    retentionPct: number
    dropoffPct: number
  }>
  ads: Array<{ date: string; cost: number }>
}

function StageTile({
  stage,
  count,
  active,
  onClick,
}: {
  stage: Stage
  count: number
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex-1 min-w-0 px-4 py-3 text-left border-r border-neutral-200 last:border-r-0 transition-colors',
        active ? 'bg-neutral-900 text-white' : 'bg-white hover:bg-neutral-50',
      )}
    >
      <div
        className="text-[11px] font-medium mb-1 uppercase tracking-wider"
        style={{ color: STAGE_ACCENT[stage] }}
      >
        {STAGE_SHORT[stage]}
      </div>
      <div
        className={cn(
          'text-2xl font-medium tracking-tight tabular-nums',
          active ? 'text-white' : 'text-neutral-900',
        )}
      >
        {formatNumber(count)}
      </div>
    </button>
  )
}

function ChartTooltip({
  active,
  payload,
  label,
  showAds,
  stage,
}: {
  active?: boolean
  payload?: Array<{ dataKey?: string | number; value?: number; color?: string; name?: string }>
  label?: string
  showAds: boolean
  stage: Stage
}) {
  if (!active || !payload?.length) return null
  const channelEntries = payload.filter((p) => p.dataKey !== 'adsCost')
  const adsEntry = payload.find((p) => p.dataKey === 'adsCost')
  const total = channelEntries.reduce((s, p) => s + (p.value ?? 0), 0)
  return (
    <div className="rounded-md border border-neutral-200 bg-white px-3 py-2 shadow-md text-xs min-w-[180px]">
      <div className="font-medium text-neutral-900 mb-1">
        {label} · <span className="text-neutral-500">{STAGE_LABELS[stage]}</span>
      </div>
      {channelEntries.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span
            className="inline-block h-2 w-2 rounded-sm"
            style={{ background: p.color }}
          />
          <span className="text-neutral-600">{labelForChannel(String(p.name ?? p.dataKey))}</span>
          <span className="ml-auto font-medium text-neutral-900">
            {formatNumber(p.value ?? 0)}
          </span>
        </div>
      ))}
      <div className="mt-1 pt-1 border-t border-neutral-100 flex items-center gap-2">
        <span className="text-neutral-500">Total</span>
        <span className="ml-auto font-semibold text-neutral-900">{formatNumber(total)}</span>
      </div>
      {showAds && adsEntry && (
        <div className="mt-1 pt-1 border-t border-neutral-100 flex items-center gap-2">
          <span className="inline-block h-2 w-2 rounded-sm bg-[#D93025]" />
          <span className="text-neutral-600">Ads spend</span>
          <span className="ml-auto font-medium text-[#D93025]">
            {formatCurrency(adsEntry.value ?? 0)}
          </span>
        </div>
      )}
    </div>
  )
}

export function AttributionChart({
  data,
  isLoading,
  showAds,
  showAdsToggle = true,
  showChannelFilter = true,
  emptyMessage = 'No attribution data in range.',
  onShowAdsChange,
}: {
  data: Dashboard | undefined
  isLoading: boolean
  showAds: boolean
  showAdsToggle?: boolean
  showChannelFilter?: boolean
  emptyMessage?: string
  onShowAdsChange?: (next: boolean) => void
}) {
  const [activeStage, setActiveStage] = useState<Stage>('visitors')
  const [hiddenChannels, setHiddenChannels] = useState<Set<string>>(new Set())

  const channels = data?.channels ?? []
  const visibleChannels = channels.filter((c) => !hiddenChannels.has(c))

  const chartData = useMemo(() => {
    if (!data) return []
    return data.series.map((row, i) => {
      const out: Record<string, number | string> = {
        date: row.date,
        label: formatDate(row.date),
      }
      for (const ch of channels) {
        out[ch] = row.perChannel[activeStage][ch] ?? 0
      }
      out.adsCost = data.ads[i]?.cost ?? 0
      return out
    })
  }, [data, channels, activeStage])

  const toggleChannel = (ch: string) => {
    setHiddenChannels((prev) => {
      const next = new Set(prev)
      if (next.has(ch)) next.delete(ch)
      else next.add(ch)
      return next
    })
  }

  return (
    <Card className="overflow-hidden p-0">
      {/* Stage tiles */}
      <div className="flex border-b border-neutral-200">
        {STAGE_ORDER.map((s) => (
          <StageTile
            key={s}
            stage={s}
            count={data?.stageTotals[s] ?? 0}
            active={activeStage === s}
            onClick={() => setActiveStage(s)}
          />
        ))}
      </div>

      {/* Drop-off bar */}
      <div className="flex border-b border-neutral-200 bg-neutral-50/60">
        {STAGE_ORDER.map((s, i) => {
          if (i === STAGE_ORDER.length - 1)
            return (
              <div
                key={s}
                className="flex-1 px-4 py-2 border-r border-neutral-200 last:border-r-0"
              />
            )
          const drop = data?.dropoffs[i]
          return (
            <div
              key={s}
              className="flex-1 px-4 py-2 border-r border-neutral-200 last:border-r-0 flex items-center gap-2 text-[11px] text-neutral-500"
            >
              <ArrowRight className="h-3 w-3 text-neutral-400" />
              <span className="font-medium text-neutral-700">
                {drop ? formatPct(drop.retentionPct) : '—'}
              </span>
              <span className="text-neutral-400">retain</span>
              <span className="ml-auto text-rose-600 font-medium">
                {drop ? `−${formatPct(drop.dropoffPct)}` : ''}
              </span>
            </div>
          )
        })}
      </div>

      {/* Channel filter chips + ads toggle */}
      {(showChannelFilter || showAdsToggle) && (
        <div className="flex flex-wrap items-center gap-1.5 px-4 py-3 border-b border-neutral-200">
          {showChannelFilter && (
            <>
              <span className="text-[11px] font-medium text-neutral-500 uppercase tracking-wider self-center mr-1">
                Channel
              </span>
              {channels.length === 0 && !isLoading && (
                <span className="text-xs text-neutral-400">No channels in range</span>
              )}
              {channels.map((ch) => {
                const hidden = hiddenChannels.has(ch)
                const color = colorForChannel(ch)
                return (
                  <button
                    key={ch}
                    onClick={() => toggleChannel(ch)}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-all',
                      hidden
                        ? 'border-neutral-200 bg-white text-neutral-400 line-through'
                        : 'border-transparent text-white',
                    )}
                    style={
                      hidden
                        ? undefined
                        : { background: color, boxShadow: `0 1px 2px ${color}33` }
                    }
                  >
                    <span
                      className="inline-block h-2 w-2 rounded-full"
                      style={{ background: hidden ? color : '#ffffff' }}
                    />
                    {labelForChannel(ch)}
                  </button>
                )
              })}
            </>
          )}
          {showAdsToggle && onShowAdsChange && (
            <button
              onClick={() => onShowAdsChange(!showAds)}
              className={cn(
                'ml-auto inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors',
                showAds
                  ? 'bg-[#D93025] border-[#D93025] text-white hover:bg-[#B5281F]'
                  : 'bg-white border-neutral-200 text-neutral-700 hover:bg-neutral-50',
              )}
            >
              <span
                className={cn(
                  'inline-block h-2 w-2 rounded-sm',
                  showAds ? 'bg-white' : 'bg-[#D93025]',
                )}
              />
              Ads layer
            </button>
          )}
        </div>
      )}

      {/* Chart */}
      <div className="h-[420px] p-4">
        {isLoading ? (
          <div className="h-full flex items-center justify-center text-sm text-neutral-400">
            Loading…
          </div>
        ) : channels.length === 0 ? (
          <div className="h-full flex items-center justify-center flex-col gap-2 text-sm text-neutral-400">
            <span>{emptyMessage}</span>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 12, right: 24, left: 4, bottom: 0 }}>
              <defs>
                {channels.map((ch) => (
                  <linearGradient key={ch} id={`grad-${ch}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={colorForChannel(ch)} stopOpacity={0.85} />
                    <stop offset="100%" stopColor={colorForChannel(ch)} stopOpacity={0.4} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid stroke="#f1f5f9" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fill: '#94a3b8', fontSize: 11 }}
                axisLine={{ stroke: '#e2e8f0' }}
                tickLine={false}
                minTickGap={24}
              />
              <YAxis
                yAxisId="left"
                tick={{ fill: '#94a3b8', fontSize: 11 }}
                axisLine={{ stroke: '#e2e8f0' }}
                tickLine={false}
                width={44}
              />
              {showAds && (
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tickFormatter={(v) => `$${v.toFixed(0)}`}
                  tick={{ fill: '#D93025', fontSize: 11 }}
                  axisLine={{ stroke: '#e2e8f0' }}
                  tickLine={false}
                  width={48}
                />
              )}
              <Tooltip
                content={(p: any) => (
                  <ChartTooltip {...p} showAds={showAds} stage={activeStage} />
                )}
              />
              <Legend
                wrapperStyle={{ fontSize: '11px', paddingTop: 8 }}
                iconType="square"
                formatter={(value: string) =>
                  value === 'adsCost' ? 'Ads spend' : labelForChannel(value)
                }
              />
              {visibleChannels.map((ch) => (
                <Area
                  key={ch}
                  yAxisId="left"
                  type="monotone"
                  dataKey={ch}
                  name={ch}
                  stackId="1"
                  stroke={colorForChannel(ch)}
                  strokeWidth={1.5}
                  fill={`url(#grad-${ch})`}
                  isAnimationActive={false}
                />
              ))}
              {showAds && (
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="adsCost"
                  stroke="#D93025"
                  strokeWidth={2}
                  strokeDasharray="4 3"
                  dot={false}
                  activeDot={{ r: 4 }}
                  isAnimationActive={false}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </Card>
  )
}
